import type { Booking } from '@court-sync/shared';

/** Bookings created in one multi-session checkout are sequential; cluster within this gap. */
export const CAMP_DAY_PURCHASE_CLUSTER_MAX_GAP_MS = 120_000;

export type CampClusterBooking = Pick<
  Booking,
  | 'id'
  | 'lessonId'
  | 'studentId'
  | 'termId'
  | 'customerId'
  | 'sessionDate'
  | 'campBookingType'
  | 'status'
  | 'createdAt'
>;

export function campPerSessionGroupKey(b: CampClusterBooking): string {
  return `${b.lessonId}:${b.studentId}:${b.termId ?? ''}:${b.customerId}`;
}

export function isCampDailySessionBooking(
  b: CampClusterBooking,
  lessonTypeById: Record<string, string | undefined>
): boolean {
  const lessonType = lessonTypeById[b.lessonId];
  return Boolean(
    b.sessionDate &&
      lessonType === 'camp' &&
      (b.campBookingType === 'daily' || b.campBookingType == null)
  );
}

function flushCampSessionCluster(
  cur: CampClusterBooking[],
  hiddenIds: Set<string>,
  clusterByPrimaryId: Map<string, CampClusterBooking[]>
): void {
  if (cur.length > 1) {
    clusterByPrimaryId.set(cur[0].id, cur);
    for (let i = 1; i < cur.length; i++) {
      hiddenIds.add(cur[i].id);
    }
  }
}

export function buildCampSessionClusterByPrimaryId(
  bookings: CampClusterBooking[],
  lessonTypeById: Record<string, string | undefined>
): Map<string, CampClusterBooking[]> {
  const { clusterByPrimaryId } = buildCampSessionClustersWithVisibility(bookings, lessonTypeById);
  return clusterByPrimaryId;
}

export function buildCampSessionClustersWithVisibility(
  bookings: CampClusterBooking[],
  lessonTypeById: Record<string, string | undefined>
): {
  clusterByPrimaryId: Map<string, CampClusterBooking[]>;
  hiddenIds: Set<string>;
} {
  const byKey = new Map<string, CampClusterBooking[]>();
  for (const b of bookings) {
    if (!isCampDailySessionBooking(b, lessonTypeById)) continue;
    const k = campPerSessionGroupKey(b);
    const arr = byKey.get(k) ?? [];
    arr.push(b);
    byKey.set(k, arr);
  }

  const hiddenIds = new Set<string>();
  const clusterByPrimaryId = new Map<string, CampClusterBooking[]>();

  for (const arr of byKey.values()) {
    arr.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    let cur: CampClusterBooking[] = [];
    for (const b of arr) {
      if (cur.length === 0) {
        cur = [b];
        continue;
      }
      const last = cur[cur.length - 1];
      const gap = new Date(b.createdAt).getTime() - new Date(last.createdAt).getTime();
      if (gap <= CAMP_DAY_PURCHASE_CLUSTER_MAX_GAP_MS) {
        cur.push(b);
      } else {
        flushCampSessionCluster(cur, hiddenIds, clusterByPrimaryId);
        cur = [b];
      }
    }
    flushCampSessionCluster(cur, hiddenIds, clusterByPrimaryId);
  }

  return { clusterByPrimaryId, hiddenIds };
}

export interface BookingClusterActionIds {
  pendingInCluster: string[];
  confirmedInCluster: string[];
  completedInCluster: string[];
  cancellableIds: string[];
  campSessionCluster: CampClusterBooking[] | undefined;
}

export function getBookingClusterActionIds(
  booking: CampClusterBooking,
  clusterByPrimaryId: Map<string, CampClusterBooking[]>
): BookingClusterActionIds {
  const campSessionCluster = clusterByPrimaryId.get(booking.id);
  return {
    campSessionCluster,
    pendingInCluster:
      campSessionCluster != null
        ? campSessionCluster.filter((b) => b.status === 'pending').map((b) => b.id)
        : booking.status === 'pending'
          ? [booking.id]
          : [],
    confirmedInCluster:
      campSessionCluster != null
        ? campSessionCluster.filter((b) => b.status === 'confirmed').map((b) => b.id)
        : booking.status === 'confirmed'
          ? [booking.id]
          : [],
    completedInCluster:
      campSessionCluster != null
        ? campSessionCluster.filter((b) => b.status === 'completed').map((b) => b.id)
        : booking.status === 'completed'
          ? [booking.id]
          : [],
    cancellableIds:
      campSessionCluster != null
        ? campSessionCluster
            .filter((b) => b.status === 'pending' || b.status === 'confirmed')
            .map((b) => b.id)
        : booking.status === 'pending' || booking.status === 'confirmed'
          ? [booking.id]
          : [],
  };
}
